/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Heart, MessageCircle } from 'lucide-react';
import { useFilters } from '../../contexts/FiltersContext';
import { usePageInsights } from '../../hooks/usePageInsights';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import AIInsightPanel from '../../components/AIInsightPanel';
import { ZodError } from 'zod';
import { BodyGetBrandPostsApiV3MetricsBrandsBrandIdPostsPost, getBrandPostsApiV3MetricsBrandsBrandIdPostsPost, GetBrandPostsApiV3MetricsBrandsBrandIdPostsPostData } from '../../config/api';

interface SocialPost {
  id: string;
  platform: 'tiktok' | 'instagram' | 'facebook' | 'twitter' | 'youtube';
  content: string;
  author: string;
  date: string;
  likes: number;
  comments: number;
  language: string;
  sentiment: 'positive' | 'negative' | 'neutral';
  postType: 'brand' | 'community';
  imageIndex: number;
}

// Placeholder images using gradients
const placeholderImages = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
  'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
  'linear-gradient(135deg, #fddb92 0%, #d1fdff 100%)',
  'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
  'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
  'linear-gradient(135deg, #ebc0fd 0%, #d9ded8 100%)',
];

// Mock social posts data
const generateMockPosts = (brandName: string): SocialPost[] => {
  const platforms = ['tiktok', 'instagram', 'facebook', 'twitter', 'youtube'] as const;
  const languages = ['EN', 'AR', 'ES', 'FR', 'DE'];

  const postTemplates = [
    { content: `Just tried ${brandName} for the first time and I'm absolutely loving it! The quality is incredible.`, sentiment: 'positive' as const, author: 'sarah_lifestyle' },
    { content: `${brandName} has been my go-to choice for years. Consistent quality that never disappoints.`, sentiment: 'positive' as const, author: 'mike_reviews' },
    { content: `Had a great experience with ${brandName} customer service today. They went above and beyond!`, sentiment: 'positive' as const, author: 'emma_wilson' },
    { content: `Anyone else notice ${brandName} has changed their formula? Not sure how I feel about it yet...`, sentiment: 'neutral' as const, author: 'davidb_tech' },
    { content: `Comparing ${brandName} with competitors and honestly it holds up really well in quality.`, sentiment: 'positive' as const, author: 'lisa_foodie' },
    { content: `${brandName}'s new campaign is everywhere! What do you all think about their new direction?`, sentiment: 'neutral' as const, author: 'james_taylor' },
    { content: `Disappointed with my recent ${brandName} purchase. Expected better quality for the price.`, sentiment: 'negative' as const, author: 'amy_roberts' },
    { content: `${brandName} just dropped their new product line and it's already sold out! The hype is real.`, sentiment: 'positive' as const, author: 'chris_lee' },
    { content: `Switching from ${brandName} to try something new. Anyone have recommendations?`, sentiment: 'negative' as const, author: 'nicole_adams' },
    { content: `${brandName} x limited edition collab is fire! Already got mine. Who else copped?`, sentiment: 'positive' as const, author: 'tom_anderson' },
    { content: `The new ${brandName} packaging is so much better. Love the sustainable materials!`, sentiment: 'positive' as const, author: 'munniajaan' },
    { content: `${brandName} prices keep going up but the quality stays the same... just saying.`, sentiment: 'negative' as const, author: 'foodie_adventures' },
    { content: `Family gathering with ${brandName} products. Nothing beats a classic! #WeekendVibes`, sentiment: 'positive' as const, author: 'weekend_chef' },
    { content: `${brandName} really understands their customers. Their loyalty program is actually worth it.`, sentiment: 'positive' as const, author: 'smart_shopper' },
    { content: `Waiting for ${brandName} to bring back their original recipe. The new one is just okay.`, sentiment: 'neutral' as const, author: 'classic_taste' },
  ];

  const dates = [
    'Dec 28, 2025', 'Dec 27, 2025', 'Dec 26, 2025', 'Dec 25, 2025', 'Dec 24, 2025',
    'Dec 23, 2025', 'Dec 22, 2025', 'Dec 20, 2025', 'Dec 18, 2025', 'Dec 15, 2025',
    'Dec 10, 2025', 'Dec 5, 2025', 'Dec 1, 2025', 'Nov 28, 2025', 'Nov 25, 2025'
  ];

  const postTypes = ['brand', 'community'] as const;

  return postTemplates.map((template, index) => {
    const likes = Math.floor(Math.random() * 500) + 1;
    const comments = Math.floor(Math.random() * 50) + 1;

    return {
      id: `post-${index + 1}`,
      platform: platforms[index % platforms.length],
      content: template.content,
      author: template.author,
      date: dates[index],
      likes,
      comments,
      language: languages[index % languages.length],
      sentiment: template.sentiment,
      postType: postTypes[index % postTypes.length],
      imageIndex: index,
    };
  });
};

const platformConfig = {
  tiktok: {
    name: 'TikTok',
    icon: 'fab fa-tiktok',
    bgClass: 'bg-black',
    textClass: 'text-white'
  },
  instagram: {
    name: 'Instagram',
    icon: 'fab fa-instagram',
    bgClass: 'bg-gradient-to-tr from-purple-600 via-pink-500 to-orange-400',
    textClass: 'text-white'
  },
  facebook: {
    name: 'Facebook',
    icon: 'fab fa-facebook-f',
    bgClass: 'bg-blue-600',
    textClass: 'text-white'
  },
  twitter: {
    name: 'X',
    icon: 'fab fa-x-twitter',
    bgClass: 'bg-black',
    textClass: 'text-white'
  },
  youtube: {
    name: 'YouTube',
    icon: 'fab fa-youtube',
    bgClass: 'bg-red-600',
    textClass: 'text-white'
  },
  linkedin: {
    name: 'LinkedIn',
    icon: 'fab fa-linkedin',
    bgClass: 'bg-blue-600',
    textClass: 'text-white'
  },

};

interface SelectProps<T extends string> {
  selectedId: string,
  onSelect: (id: T) => void,
  values: {id: T, title: string}[],
  className: string
}
function SingleSelect<T extends string>({selectedId, onSelect, values, className}: SelectProps<T>) {
  return (
    <select
      value={selectedId}
      onChange={(e) => onSelect(e.target.value as T)}
      className={className}
    >
      {values.map(({id, title}) => (
        <option key={id} value={id}>{title}</option>
      ))}
    </select>
  )
}

type BodyParam<T extends keyof BodyGetBrandPostsApiV3MetricsBrandsBrandIdPostsPost> = Exclude<BodyGetBrandPostsApiV3MetricsBrandsBrandIdPostsPost[T], undefined>
type QueryParam<T extends keyof Exclude<GetBrandPostsApiV3MetricsBrandsBrandIdPostsPostData['query'], undefined>> = Exclude<Exclude<GetBrandPostsApiV3MetricsBrandsBrandIdPostsPostData['query'], undefined>[T], undefined>
const PostsDashboard: React.FC = () => {
  const [selectedPlatform, setSelectedPlatform] = useState<BodyParam<'platform'>>([]);
  const [selectedSentiment, setSelectedSentiment] = useState<BodyParam<'sentiment'>>([]);
  const [selectedPostType, setSelectedPostType] = useState<QueryParam<'data_source'>>('all');
  const [sortBy, setSortBy] = useState<QueryParam<'order_by'>>('recent');
  const { selectedBrand, selectedFilters, getFiltersByType } = useFilters()
  const { insights: pageInsights, loading: pageInsightsLoading } = usePageInsights('posts');

  const {data, fetchNextPage, hasNextPage, isFetchingNextPage} = useInfiniteQuery({
    queryKey: ['v3', 'digital-posts', selectedBrand, selectedFilters, selectedPlatform, selectedSentiment, selectedPostType, sortBy],
    queryFn: ({ pageParam }) => getBrandPostsApiV3MetricsBrandsBrandIdPostsPost({
      path: {brand_id: selectedBrand!},
      body: {
        filters: getFiltersByType('digital'),
        platform: selectedPlatform,
        sentiment: selectedSentiment,
      },
      query: {
        data_source: selectedPostType,
        order_by: sortBy,
        limit: 12,
        cursor: pageParam,
      }
    }).then(res => {
      if (res instanceof ZodError) {
        console.log(res)
        throw res
      }
      if (res.data) {
        return res.data
      } else {
        return {
          data: [],
          nextCursor: null
        }
      }
    }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!selectedBrand,
  })


  useEffect(() => console.log(selectedPlatform), [selectedPlatform])

  // const posts = useMemo(() => generateMockPosts(selectedBrands[0]), [selectedBrands]);

  // const filteredPosts = useMemo(() => {
  //   let result = [...posts];

  //   if (selectedPlatform !== 'all') {
  //     result = result.filter(post => post.platform === selectedPlatform);
  //   }

  //   if (selectedSentiment !== 'all') {
  //     result = result.filter(post => post.sentiment === selectedSentiment);
  //   }

  //   if (selectedPostType !== 'all') {
  //     result = result.filter(post => post.postType === selectedPostType);
  //   }

  //   if (sortBy === 'engagement') {
  //     result.sort((a, b) => (b.likes + b.comments) - (a.likes + a.comments));
  //   }

  //   return result;
  // }, [posts, selectedPlatform, selectedSentiment, selectedPostType, sortBy]);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {/* Platform Selector */}
        <div className="relative">
          <select
            value={selectedPlatform.at(0) || 'all'}
            onChange={(e) => setSelectedPlatform(e.target.value === "all"? []: [e.target.value as BodyParam<'platform'>[number]])}
            className="appearance-none bg-card border border-[var(--border)] rounded-full px-3 py-1.5 pr-7 text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 hover:border-primary/30 transition-all cursor-pointer"
          >
            <option value="all">All Platforms</option>
            <option value="Tiktok">TikTok</option>
            <option value="Instagram">Instagram</option>
            <option value="Facebook">Facebook</option>
            <option value="Twitter">X (Twitter)</option>
            <option value="Youtube">YouTube</option>
            <option value="Linkedin">Linkedin</option>
          </select>
          <i className="fas fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted text-[10px] pointer-events-none"></i>
        </div>

        {/* Sentiment Selector */}
        <div className="relative">
          <select
            value={selectedSentiment.at(0) || 'all'}
            onChange={(e) => setSelectedSentiment(e.target.value === "all"? []: [e.target.value as BodyParam<'sentiment'>[number]])}
            className="appearance-none bg-card border border-[var(--border)] rounded-full px-3 py-1.5 pr-7 text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 hover:border-primary/30 transition-all cursor-pointer"
          >
            <option value="all">All Sentiments</option>
            <option value="Positive">Positive</option>
            <option value="Neutral">Neutral</option>
            <option value="Negative">Negative</option>
          </select>
          <i className="fas fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted text-[10px] pointer-events-none"></i>
        </div>

        {/* Post Type Selector */}
        <div className="relative">
          <SingleSelect values={[
              {id: 'all', title: 'All Types'},
              {id: 'Brand', title: 'Brand'},
              {id: 'Community', title: 'Community'},
            ]}
            onSelect={id => setSelectedPostType(id)}
            selectedId={selectedPostType}
            className="appearance-none bg-card border border-[var(--border)] rounded-full px-3 py-1.5 pr-7 text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 hover:border-primary/30 transition-all cursor-pointer"
          />
          <i className="fas fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted text-[10px] pointer-events-none"></i>
        </div>

        {/* Sort By Selector */}
        <div className="relative">
          <SingleSelect
            values={[
              {id: 'recent', title: 'Most Recent'},
              {id: 'engagement', title: 'Highest Engagement'},
            ]}
            onSelect={id => setSortBy(id)}
            selectedId={sortBy}
            className="appearance-none bg-card border border-[var(--border)] rounded-full px-3 py-1.5 pr-7 text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 hover:border-primary/30 transition-all cursor-pointer"
          />
          <i className="fas fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted text-[10px] pointer-events-none"></i>
        </div>
      </div>

      {/* Posts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {data?.pages?.map((page, i) =>
          page.data.map(post => {
            const platform = platformConfig[post.platform.toLowerCase() as keyof typeof platformConfig];
            return (
              <div
                key={post.id}
                className="bg-card rounded-2xl border-2 border-purple-500/30 overflow-hidden hover:border-purple-500/60 transition-all shadow-lg hover:shadow-purple-500/20"
              >
                {/* Image Section */}
                <div
                  className="relative h-52 w-full"
                  style={{ background: placeholderImages[i % placeholderImages.length] }}
                >
                  {/* Platform Badge - Top Left */}
                  <div className={`absolute top-3 left-3 ${platform.bgClass} ${platform.textClass} px-3 py-1.5 rounded-full flex items-center gap-2 text-sm font-medium shadow-lg`}>
                    <i className={platform.icon}></i>
                    <span>{platform.name}</span>
                  </div>

                  {/* Language Badge - Top Right */}
                  <div className="absolute top-3 right-3 bg-purple-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold shadow-lg">
                    {post.language}
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-4">
                  {/* Author */}
                  <h3 className="text-foreground font-semibold text-lg mb-2">{post.author}</h3>

                  {/* Caption */}
                  <p className="text-text-muted text-sm leading-relaxed line-clamp-2">{post.content}</p>
                </div>

                {/* Footer */}
                <div className="px-4 pb-4 pt-2">
                  <div className="flex items-center justify-between text-text-muted">
                    {/* Engagement Stats */}
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1.5 text-sm">
                        <Heart className="w-4 h-4 text-pink-500 fill-pink-500" />
                        <span className="text-foreground">{post.likes}</span>
                      </span>
                      <span className="flex items-center gap-1.5 text-sm">
                        <MessageCircle className="w-4 h-4 text-blue-500" />
                        <span className="text-foreground">{post.comments}</span>
                      </span>
                    </div>

                    {/* Platform Icon & Date */}
                    <div className="flex items-center gap-2 text-sm">
                      <i className={`${platform.icon} text-purple-400`}></i>
                      <span className="text-text-muted">{post.date}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Load More Button */}
      {hasNextPage && (
        <div className="flex justify-center pt-4">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="bg-card border border-[var(--border)] rounded-full px-6 py-2 text-sm font-medium text-foreground hover:border-primary/30 hover:bg-purple-500/10 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isFetchingNextPage ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
      <AIInsightPanel title="AI Insights" insights={pageInsights} loading={pageInsightsLoading} />
    </div>
  );
};

export default PostsDashboard;
